// Connectivity vs reachability. WiFi can be up with no path to Lion — isOnline() means we can talk to the API.
import axios from "axios";
import { Network } from "@capacitor/network";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import _ from "lodash";

let connected = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
let reachable = connected;
let started = false;
let probing = false;
const onlineListeners = [];
const statusListeners = [];

export const isOnline = () => reachable;
export const isConnected = () => connected;

export const isNetworkError = (error) =>
  !error?.response ||
  error.code === "ECONNABORTED" ||
  error.message === "Network Error" ||
  _.includes(_.toLower(error?.message || ""), "timeout");

export const onOnline = (cb) => {
  onlineListeners.push(cb);
  return () => _.pull(onlineListeners, cb);
};

export const onStatusChange = (cb) => {
  statusListeners.push(cb);
  return () => _.pull(statusListeners, cb);
};

const setReachable = (next) => {
  const was = reachable;
  reachable = !!next;
  if (was === reachable) return;
  _.each(statusListeners, (cb) => cb(reachable));
  if (!was && reachable) {
    _.each(onlineListeners, (cb) => cb());
  }
};

export const markReachable = () => setReachable(true);
export const markUnreachable = () => setReachable(false);

export const probeReachability = async () => {
  if (!connected) {
    setReachable(false);
    return false;
  }
  if (probing) return reachable;
  probing = true;
  try {
    // Any HTTP response (incl. 401) means the API is reachable. Timeout/network = not.
    await axios.get("/auth/me.json", {
      skipOffline: true,
      skipOfflineWrite: true,
      timeout: 5000,
      validateStatus: () => true,
    });
    setReachable(true);
    return true;
  } catch (_e) {
    setReachable(false);
    return false;
  } finally {
    probing = false;
  }
};

const setConnected = (next, { probe = true } = {}) => {
  connected = !!next;
  if (!connected) {
    setReachable(false);
    return;
  }
  if (probe) probeReachability();
};

export const startNetworkWatcher = async ({ onReconnect, onResumeSync } = {}) => {
  if (started) return;
  started = true;

  if (onReconnect) onOnline(onReconnect);

  if (Capacitor.isNativePlatform()) {
    try {
      const status = await Network.getStatus();
      setConnected(status.connected, { probe: true });
      await Network.addListener("networkStatusChange", (status) => {
        setConnected(status.connected, { probe: true });
      });
      await App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) return;
        if (connected) probeReachability();
        if (reachable && onResumeSync) onResumeSync();
      });
    } catch (_e) {
      // fall through to browser events
    }
  }

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => setConnected(true, { probe: true }));
    window.addEventListener("offline", () => setConnected(false));
  }

  if (connected) probeReachability();
};
